import { Dialog } from 'primereact/dialog'
import React, { useState, useMemo, useCallback, useRef } from 'react'
import AddBankAccount from './AddBankAccount'
import { useTranslation } from 'react-i18next'
import TooltipOverlay from '../../global/TooltipOverlay'
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons'
import mainAxios from '../../../services/mainAxios'
import PrimeTable from '../../PrimeTable/PrimeTable'
import { toast } from "react-toastify";
import currencies from "../../../services/currencies.json"
import ExportCsvPrime from '../../global/ExportCsvPrime'
import { usePersistedState } from '../../../AppContext'
import CellInput from '../../global/CellInput'


const BankAccounts = ({company,setCompany,getCompany}) => {
  const [addBankAccountVisible, setAddBankAccountVisible] = useState({show:false,id:null})
  const { t, i18n } = useTranslation("translations");
  const [reorderRow, setReorderRow] = useState(null);
  const [loading,setLoading]=useState(false)



  const defaultBankAccountId = useMemo(() => {
    const defaults = (company.bankAccounts ?? []).filter((ba) => ba.default);
    if (defaults.length <= 1) return defaults[0]?.id;
    // Data invariant is "exactly one default", but the backend can return several.
    // find()/first-in-order would make the badge jump to a different account whenever
    // rows are reordered. Pick deterministically (lowest id) so reordering never
    // changes which account is shown as default.
    return defaults.reduce((min, ba) => (ba.id < min.id ? ba : min)).id;
  }, [company.bankAccounts]);

  const setDefaultBankAccount = (e, bankAccountId) => {
    e.preventDefault();
    const index = company.bankAccounts.findIndex(
      (ba) => ba.id === bankAccountId
    );
    const bankAccounts = [
      ...company.bankAccounts
        .slice(0, index)
        .map((bankAccount) => ({ ...bankAccount, default: false })),
      {
        ...company.bankAccounts[index],
        default: true,
      },
      ...company.bankAccounts
        .slice(index + 1)
        .map((bankAccount) => ({ ...bankAccount, default: false })),
    ];

    mainAxios
      .post("apiEndpoint/saveOrUpdate", {
        object: "SetDefaultBankAccount",
        operation: JSON.stringify({ id: bankAccountId }),
        nuis: company.nuis,
      })
      .then((res) => {
        if (res?.data?.status === true) {
          toast.success("Bank Account was set as default", {
            containerId: "all_toast",
          });
          setCompany((state) => ({
              ...state,
              bankAccounts,
          }));
        }
      });
  };

  const bankAccountColumns = [
    {
      header: t(["home.actions"]),
      field: "actions",
      style:{minWidth: "110px",maxWidth:"130px"},
      default: true,
      persisted: true,
      mobile: { priority: "action", group: "actions" },
      body: (bankAccount) => (
        <div className='d-flex align-items-center'>
          <TooltipOverlay title={t("common.edit")} fixed placement="top">
            <Link
                href="#"
                onClick={(e)=>{
                  e.preventDefault()
                  setAddBankAccountVisible({show:true,id:bankAccount.id.toString()})
                }
                }
              // to={`/companies/${company.id}/bank-accounts/${bankAccount.id}`}
                className="nav-link p-0 mr-3"
            >
              <FontAwesomeIcon icon={faEdit} className="mr-1" />
            </Link>
          </TooltipOverlay>
          {bankAccount.id !== defaultBankAccountId ? (
            <TooltipOverlay title={t("company.setDefault")} fixed placement="top">
              <Link
                href="#"
                className="nav-link p-0"
                onClick={(e) => {
                  setDefaultBankAccount(e, bankAccount.id);
                }}
              >
                <FontAwesomeIcon icon={faCheck} className="mr-1" />
              </Link>
            </TooltipOverlay>
          ) : (
            <div className="badge badge-success">Default</div>
          )}
        </div>
      ),
    },
    {
      header: t(["bankAccount.accountName"]),
      filter: true,
      field: "bankName",
      style:{minWidth: "200px",maxWidth:"240px"},
      sortable: true,
      default: true,
      persisted: true,
      mobile: { priority: 1, isTitle: true, group: "identity" },
      body: (bankAccount) => bankAccount.bankName || "",
    },
    {
      header: "Bank Account Number",
      filter: true,
      field: "bankAccountNumber",
      style:{minWidth: "250px",maxWidth:"280px"},
      sortable: true,
      default: true,
      mobile: { priority: 1, group: "identity" },
      body: (bankAccount) => <CellInput value={bankAccount.bankAccountNumber ?? ""} />,
    },
    {
      header: "IBAN",
      field: "iban",
      filter: true,
      style:{minWidth: "180px",maxWidth:"220px"},
      sortable: true,
      default: true,
      mobile: { priority: 1, group: "identity" },
      body: (bankAccount) => bankAccount.iban || "",
    },
    {
      header: t(["invoice.currency"]),
      field: "currency",
      filter: true,
      sortable: true,
      default: true,
      style:{minWidth: "150px",maxWidth:"180px"},
      mobile: { priority: 1, isBadge: true, group: "status" },
      filter:true,
      filterType:"DROPDOWN",
      filterConfig: {
        filter:true,
        options: currencies,
      },
      body: (bankAccount) => bankAccount.currency || "",
    },

    {
      header: t(["bankAccount.includeInInvoice"]),
      sortable: true,
      style:{minWidth: "200px",maxWidth:"220px"},
      field: "includeInInvoice",
      default: true,
      mobile: { priority: 1, group: "status" },
      filter:true,
      filterType:"TRISTATECHECKBOX",
      dataType:"boolean",
    },
    {
      header: t(["bankAccount.activate"]),
      sortable: true,
      style:{minWidth: "130px",maxWidth:"150px"},
      default: true,
      mobile: { priority: 1, group: "status" },
      filter:true,
      filterType:"TRISTATECHECKBOX",
      dataType:"boolean",
      field: "active",
    },
  ];

  const [selectedColumns, setSelectedColumns] = usePersistedState(
    bankAccountColumns.map((col) => ({ field: col.field, show: !!col.default, ...(col?.persisted ? { persisted: true } : {}) })),
    "bankAccountsColumnsPrime1"
  );

  const conditionalBankAccountRowStyles = [
    {
      when: (account) => account?.default,
      style: {
        backgroundColor: "#d4edda",
        color: "#155724",
        "&:hover": {
          cursor: "#155724",
        },
      },
    },
  ];

  const saveReorderRows = (newBankAccounts) => {
    setLoading(true)
    mainAxios
      .post('apiEndpoint/saveOrUpdate', {
        object: 'ReorderBankAccounts',
        nuis: company.nuis,
        content: newBankAccounts.map(({id,orderNo})=>({ id,orderNo })),
      })
      .then((res) => {
        if(res.data.status){
          setCompany((company)=> ({
            ...company,
            bankAccounts:newBankAccounts
          }));
        }
        setLoading(false)
        toast.success(t("invoice.bankAccOrder"), {
          containerId: "all_toast",
        });
      })
      .catch((err) => {
        console.log(err);
      });
      
  };
  


  const orderedBankAccounts=useMemo(()=>{
    // copy before sorting — .sort() mutates in place, and mutating company.bankAccounts
    // makes React miss the change on reorder (stale "Default" badge on the wrong row)
    return [...(company?.bankAccounts ?? [])].sort((a, b) => a.orderNo - b.orderNo);
  },[company?.bankAccounts])

    // PrimeReact doesn't call setDragImage for row reorder, so the browser falls back to
    // snapshotting the draggable <tr>, which renders garbled ("2 rows") in a scrollable
    // table. A 1x1 transparent Image is unreliable — Chromium ignores it and falls back to
    // the row snapshot. A real DOM element IS honored, so use a clean single pill labelled
    // with the account name (skip the Actions cell, whose text is the "Default" badge).
    const dragGhostRef = useRef(null);

    const handleRowDragStart = useCallback((e) => {
        const row = e.target?.closest?.("tr");
        if (!row || !e.dataTransfer) return;

        const cellTexts = Array.from(row.querySelectorAll("td"))
            .map((td) => (td.innerText || "").trim())
            .filter((txt) => txt && txt.toLowerCase() !== "default");
        const label = cellTexts[0] || t("bankAccount.accountName");

        const ghost = document.createElement("div");
        ghost.textContent = label;
        ghost.style.cssText =
            "position:absolute;top:-1000px;left:-1000px;padding:6px 14px;" +
            "background:#007567;color:#fff;border-radius:6px;" +
            "box-shadow:0 3px 8px rgba(0,0,0,.2);font-size:13px;font-weight:600;" +
            "white-space:nowrap;pointer-events:none;z-index:99999;";
        document.body.appendChild(ghost);
        dragGhostRef.current = ghost;
        e.dataTransfer.setDragImage(ghost, 12, 12);
    }, [t]);

    const handleRowDragEnd = useCallback(() => {
        if (dragGhostRef.current) {
            dragGhostRef.current.remove();
            dragGhostRef.current = null;
        }
    }, []);

    const headerButtonsLeft = (
        <div className="prime-table-toolbar__add-btn" onClick={() => setAddBankAccountVisible({ show: true, id: null })}>
            <FontAwesomeIcon icon={faPlus} />
            {t("bankAccount.add")}
        </div>
    );

    const headerIcons = (
        <ExportCsvPrime
            iconOnly
            tooltip="Export CSV"
            data={orderedBankAccounts ?? []}
            columns={bankAccountColumns}
            excludedColumns={["actions"]}
            onClick={() => Promise.resolve(true)}
        />
    );

    return (
        <>
            <Dialog
                header={addBankAccountVisible.id ? t("bankAccount.edit") : t("bankAccount.add")}
                visible={addBankAccountVisible.show}
                onHide={() => setAddBankAccountVisible((state) => ({ ...state, show: false }))}
                style={{ width: "30vw" }}
                breakpoints={{ "960px": "75vw", "641px": "100vw" }}
                closable={true}
                dismissableMask={true}

            >
                <AddBankAccount
                    company={company}
                    bankAccountId={addBankAccountVisible.id}
                    onBankAdded={() => {
                        setAddBankAccountVisible({ show: false, id: null })
                        getCompany({ reloadParameters: false, reload: true })
                    }}
                />
            </Dialog>
            {/* <Table
                columns={bankAccountColumns}
                data={[...(company.bankAccounts || [])]}
                searchableKeys={[
                    "bankName",
                    "iban",
                    "currency",
                    "includeInInvoice",
                    "default",
                ]}
                defaultSortField="modificationDate"
                conditionalRowStyles={conditionalBankAccountRowStyles}
            /> */}

  
      
        <div className="reorder-handle-smooth" onDragStartCapture={handleRowDragStart} onDragEndCapture={handleRowDragEnd}>
        <PrimeTable
          reorderableRows
          stripedRows={false}
          reorderableColumns={false}
          loading={loading}
          value={orderedBankAccounts?? []}
          columns={bankAccountColumns}
          showFilters={true}
          filterDisplay={"row"}
          responsiveLayout="scroll"
          dataKey="id"
          scrollHeight="580px"
          simpleTable={true}
          showSelectColumns
          gridKey="bankAccountsColumnsPrime1"
          selectedColumns={[selectedColumns, setSelectedColumns]}
          headerButtonsLeft={headerButtonsLeft}
          headerIcons={headerIcons}
          onRowReorder={(e) => {
            // Use PrimeReact's reordered array (e.value) and build fresh objects with
            // new orderNo — never mutate the originals, or the reordered row's derived
            // UI (e.g. the "Default" badge) can render stale/on the wrong row.
            const newBankAccounts = e.value.map((bankAccount, index) => ({
              ...bankAccount,
              orderNo: index + 1,
            }));
            saveReorderRows(newBankAccounts);
          }}
        />
        </div>


        </>
    )
}

export default BankAccounts
